import { useEffect, useMemo, useRef, useState } from 'react';
import { addMonths, addYears, format, parse, startOfDay } from 'date-fns';
import {
  CaretLeftIcon,
  CaretRightIcon,
  DeviceMobile,
  Envelope,
  Warning,
  WarningCircle,
} from '@phosphor-icons/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useToast } from '@/hooks/use-toast';
import { ConfirmationModal } from '@/components/core';
import { useGetLists } from '@/modules/lists/hooks/use-lists';
import { useGetSegments } from '@/modules/segments/hooks/use-segments';
import { useGetTemplate } from '@/modules/templates/hooks/use-templates';
import { CampaignAudienceStep } from '../components/campaign-audience-step/campaign-audience-step';
import { CampaignContentStep } from '../components/campaign-content-step/campaign-content-step';
import {
  CampaignReviewStep,
  type CampaignReviewCheck,
} from '../components/campaign-review-step/campaign-review-step';
import { CampaignScheduleStep } from '../components/campaign-schedule-step/campaign-schedule-step';
import {
  formatTimezoneLabel,
  getDefaultCampaignTimezone,
} from '../constants/timezones.constants';
import {
  DEFAULT_CAMPAIGN_FROM_EMAIL,
  DEFAULT_CAMPAIGN_FROM_NAME,
  DEFAULT_CAMPAIGN_REPLY_TO,
} from '../constants/campaigns.constants';
import {
  useCreateCampaignRest,
  useGetCampaignContentRest,
  useGetCampaignRestById,
  usePutCampaignContentRest,
  useApplyCampaignTemplateRest,
  useScheduleCampaignRest,
  useSendCampaignRest,
  useSetCampaignAudienceRest,
  useUpdateCampaignRest,
} from '../hooks/use-campaigns-rest';
import { getCampaignSendEligibility } from '../services/campaigns-rest.service';
import type {
  CreateCampaignRestInput,
  UpdateCampaignRestInput,
} from '../types/campaigns-rest.types';
import { audienceSelectionToRestInput } from '../types/campaigns-rest.types';
import { canEditCampaign } from '../utils/can-edit-campaign.utils';
import {
  buildScheduleCampaignRestInput,
  MIN_SCHEDULE_AHEAD_MS,
} from '../utils/schedule-campaign.utils';
import { getDefaultScheduleSlot } from '../utils/schedule-time.utils';
import type { CampaignScheduleDraft } from '../utils/campaign-schedule-draft.utils';
import {
  EMPTY_AUDIENCE_SELECTION,
  isAudienceSelectionValid,
  type CampaignAudienceSelection,
} from '../utils/campaign-audience.utils';
import {
  formatCampaignDate,
  shouldClearLegacySubjectAsTemplateName,
} from '../utils/campaigns-mapper.utils';
import {
  WIZARD_STEP_COUNT,
  WIZARD_STEP_VERSION,
  clearWizardStepFromSession,
  getFirstInvalidCampaignStep,
  resolveInitialWizardStep,
  writeWizardStepToSession,
} from '../utils/wizard-step.utils';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { useSidebar } from '@/components/ui-kit/sidebar';
import { cn } from '@/lib/utils';

const fromEmailSchema = z
  .string()
  .trim()
  .min(1, { message: 'From email is required' })
  .email({ message: 'Enter a valid email address.' });
const STEP_TITLES = ['Details', 'Audience', 'Content', 'Schedule', 'Review'] as const;
/**
 * TEMP design-review flag — skips campaign persistence and all wizard
 * step/publish validation so every screen can be walked freely.
 * Set back to `false` when re-enabling real save + validation.
 */
const DESIGN_PREVIEW_MODE = false;
const SUBJECT_RECOMMENDED_LENGTH = 60;
const PREHEADER_RECOMMENDED_LENGTH = 100;
const PREHEADER_MAX_LENGTH = 200;
const SEND_TYPES = ['Regular', 'Scheduled', 'Recurring'] as const;
const SEND_TYPE_CTA_LABELS: Record<SendType, string> = {
  Regular: 'Send now',
  Scheduled: 'Schedule campaign',
  Recurring: 'Start recurring campaign',
};
const RECURRING_FREQUENCIES = ['Daily', 'Weekly', 'Monthly', 'Yearly'] as const;
const TEMPLATE_TYPES = ['Saved Templates', 'Template Gallery', 'Blank Template'] as const;

const getCurrentScheduleDate = (): Date => startOfDay(new Date());

const getRecurringEndDateForFrequency = (startDate: Date, frequency: RecurringFrequency): Date => {
  const start = startOfDay(startDate);
  if (frequency === 'Monthly') {
    return startOfDay(addMonths(start, 1));
  }
  if (frequency === 'Yearly') {
    return startOfDay(addYears(start, 1));
  }
  return start;
};

type SendType = (typeof SEND_TYPES)[number];
type RecurringFrequency = (typeof RECURRING_FREQUENCIES)[number];
type TemplateType = (typeof TEMPLATE_TYPES)[number];

const formatScheduleTimeDisplay = (time: string) => {
  try {
    return format(parse(time, 'HH:mm', new Date()), 'h:mm a');
  } catch {
    return time;
  }
};

type CreateCampaignPageProps = {
  campaignId?: string;
};

export const CreateCampaignPage = ({ campaignId }: CreateCampaignPageProps = {}) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = Boolean(campaignId?.trim());
  const {
    data: campaign,
    isLoading: isCampaignLoading,
    isError: isCampaignError,
    error: campaignError,
  } = useGetCampaignRestById(campaignId ?? '', { enabled: isEditMode });
  const isCampaignNotFound =
    (campaignError as { status?: number } | null)?.status === 404 ||
    (isEditMode && !isCampaignLoading && !isCampaignError && !campaign);
  const { mutateAsync: createCampaign, isPending: isCreating } = useCreateCampaignRest();
  const { mutateAsync: updateCampaign, isPending: isUpdating } = useUpdateCampaignRest();
  const { mutateAsync: setAudience, isPending: isSettingAudience } =
    useSetCampaignAudienceRest();
  const { mutateAsync: sendCampaign, isPending: isSending } = useSendCampaignRest();
  const { mutateAsync: scheduleCampaign, isPending: isScheduling } = useScheduleCampaignRest();
  const { mutateAsync: putContent } = usePutCampaignContentRest();
  const { mutateAsync: applyTemplate } = useApplyCampaignTemplateRest();
  const isSaving = isCreating || isUpdating || isSettingAudience;
  const [isHydrated, setIsHydrated] = useState(!isEditMode);
  const [draftCampaignId, setDraftCampaignId] = useState('');
  const [currentStep, setCurrentStep] = useState(() =>
    resolveInitialWizardStep(location.state, campaignId)
  );
  const effectiveCampaignId = campaignId?.trim() || draftCampaignId.trim() || undefined;
  const [campaignName, setCampaignName] = useState('');
  const [subject, setSubject] = useState('');
  const [fromEmail, setFromEmail] = useState(DEFAULT_CAMPAIGN_FROM_EMAIL);
  const [fromName, setFromName] = useState(DEFAULT_CAMPAIGN_FROM_NAME);
  const [replyTo, setReplyTo] = useState(DEFAULT_CAMPAIGN_REPLY_TO);
  const [preheader, setPreheader] = useState('');
  const [sendType, setSendType] = useState<SendType>('Regular');
  const [scheduleDate, setScheduleDate] = useState<Date>(() => getDefaultScheduleSlot().date);
  const [scheduleTime, setScheduleTime] = useState(() => getDefaultScheduleSlot().time);
  const [timeZone, setTimeZone] = useState(() => getDefaultCampaignTimezone());
  const [recurringFrequency, setRecurringFrequency] = useState<RecurringFrequency>('Weekly');
  const [recurringStartDate, setRecurringStartDate] = useState<Date>(
    () => getDefaultScheduleSlot().date
  );
  const [recurringEndDate, setRecurringEndDate] = useState<Date>(() => getCurrentScheduleDate());
  const [audienceSelection, setAudienceSelection] =
    useState<CampaignAudienceSelection>(EMPTY_AUDIENCE_SELECTION);
  const [audienceDirty, setAudienceDirty] = useState(false);
  const [audienceCounts, setAudienceCounts] = useState({ audience: 0, sendable: 0 });
  const [templateType, setTemplateType] = useState<TemplateType>('Saved Templates');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [selectedContentId, setSelectedContentId] = useState('');
  const [lastSavedHtml, setLastSavedHtml] = useState('');
  /** True when local HTML may differ from what the server last persisted. */
  const [isContentDirty, setIsContentDirty] = useState(false);
  const [campaignNameTouched, setCampaignNameTouched] = useState(false);
  const [subjectTouched, setSubjectTouched] = useState(false);
  const [fromEmailTouched, setFromEmailTouched] = useState(false);
  const [fromNameTouched, setFromNameTouched] = useState(false);
  const [replyToTouched, setReplyToTouched] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
  const [contentEditorActive, setContentEditorActive] = useState(false);
  const sendIdempotencyKeyRef = useRef<string | null>(null);
  const lastConfirmSendableRef = useRef<number | undefined>(undefined);
  const { open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar();
  const sidebarOpenBeforeEditorRef = useRef<boolean | null>(null);

  useEffect(() => {
    writeWizardStepToSession(currentStep, effectiveCampaignId);
  }, [currentStep, effectiveCampaignId]);

  useEffect(() => {
    if (contentEditorActive) {
      if (sidebarOpenBeforeEditorRef.current === null) {
        sidebarOpenBeforeEditorRef.current = sidebarOpen;
        setSidebarOpen(false);
      }
      return;
    }

    if (sidebarOpenBeforeEditorRef.current !== null) {
      setSidebarOpen(sidebarOpenBeforeEditorRef.current);
      sidebarOpenBeforeEditorRef.current = null;
    }
  }, [contentEditorActive, setSidebarOpen, sidebarOpen]);

  useEffect(() => {
    if (DESIGN_PREVIEW_MODE) return;
    if (!isEditMode || !campaign) return;

    if (canEditCampaign(campaign.status)) return;

    toast({
      variant: 'destructive',
      title: 'Editing unavailable',
      description: "Published campaigns can't be edited.",
    });
    navigate(`/campaigns/${campaign.id}`, { replace: true });
  }, [campaign, isEditMode, navigate, toast]);

  useEffect(() => {
    if (!isEditMode || !campaign || isHydrated) return;
    if (!canEditCampaign(campaign.status)) return;

    setCampaignName(campaign.name);
    setSubject(campaign.subject);
    setFromEmail(campaign.fromEmail.trim() || DEFAULT_CAMPAIGN_FROM_EMAIL);
    setFromName(campaign.fromName.trim() || DEFAULT_CAMPAIGN_FROM_NAME);
    setReplyTo(campaign.replyTo.trim() || DEFAULT_CAMPAIGN_REPLY_TO);
    setPreheader(campaign.preheader);
    const savedSchedule = campaign.draftSchedule;
    if (savedSchedule) {
      const savedDate = parse(savedSchedule.date, 'yyyy-MM-dd', new Date());
      const savedInstant = parse(savedSchedule.time, 'HH:mm', startOfDay(savedDate)).getTime();
      // A schedule that has already slipped into the past is not restored with an error; the
      // wizard falls back to "send now" and the next save replaces the stale plan.
      if (!Number.isNaN(savedInstant) && savedInstant >= Date.now() + MIN_SCHEDULE_AHEAD_MS) {
        setSendType(savedSchedule.sendType);
        setScheduleDate(startOfDay(savedDate));
        setRecurringStartDate(startOfDay(savedDate));
        setScheduleTime(savedSchedule.time);
        setTimeZone(savedSchedule.timeZone);
        setRecurringFrequency(savedSchedule.recurringFrequency);
      }
    }
    setAudienceSelection({
      includeListIds: campaign.audience.listIds,
      includeSegmentIds: campaign.audience.segmentIds,
      excludeSegmentIds: campaign.audience.excludeSegmentIds ?? [],
      excludeListIds: campaign.audience.excludeListIds ?? [],
    });
    setAudienceCounts({
      audience: campaign.audienceSize ?? campaign.audience.estimatedRecipients ?? 0,
      sendable: campaign.sendableCount ?? campaign.audience.sendableCount ?? 0,
    });
    setSelectedTemplateId(campaign.templateIds[0] ?? '');
    setSelectedContentId(campaign.contentIds[0] ?? '');
    setIsHydrated(true);
  }, [campaign, isEditMode, isHydrated]);

  const { data: campaignContent } = useGetCampaignContentRest(effectiveCampaignId, {
    // Content is step 3+. Fetching earlier 404s for drafts with no template yet and toasts "Something went wrong".
    enabled:
      isEditMode &&
      isHydrated &&
      Boolean(effectiveCampaignId) &&
      !lastSavedHtml.trim() &&
      currentStep >= 3 &&
      (campaign?.contentHasHtml === true ||
        Boolean(campaign?.contentIds?.length) ||
        Boolean(selectedContentId.trim())),
  });

  useEffect(() => {
    if (!campaignContent?.html?.trim() || lastSavedHtml.trim()) return;
    setLastSavedHtml(campaignContent.html);
    if (campaignContent.contentId) {
      setSelectedContentId(campaignContent.contentId);
    }
    setIsContentDirty(false);
  }, [campaignContent, lastSavedHtml]);

  /**
   * The template name is display-only state: it used to be smuggled through
   * `CampaignMediaDetails.Subject`, which now holds the real email subject. On
   * an edit load the name comes back from the template the campaign points at.
   */
  const shouldLookupTemplateName = Boolean(selectedTemplateId.trim()) && !selectedTemplateName;
  const { data: selectedTemplate } = useGetTemplate(selectedTemplateId, {
    enabled: shouldLookupTemplateName,
  });

  useEffect(() => {
    if (!shouldLookupTemplateName || !selectedTemplate) return;
    const name = selectedTemplate.name?.trim();
    if (name) {
      setSelectedTemplateName(name);
      if (!subjectTouched && shouldClearLegacySubjectAsTemplateName(subject, name)) {
        setSubject('');
      }
    }
    if (selectedTemplate.contentId?.trim() && !selectedContentId.trim()) {
      setSelectedContentId(selectedTemplate.contentId.trim());
    }
    if (!lastSavedHtml.trim() && selectedTemplate.html?.trim()) {
      setLastSavedHtml(selectedTemplate.html);
    }
  }, [
    lastSavedHtml,
    selectedContentId,
    selectedTemplate,
    shouldLookupTemplateName,
    subject,
    subjectTouched,
  ]);

  /**
   * "Use in a campaign" from the Templates list navigates here with a
   * `presetTemplateId` in nav state. Loads that template's current content and
   * applies it exactly like picking it from the in-wizard gallery — a one-time
   * copy on this fresh campaign, never re-applied on an edit-mode reload (which
   * hydrates its own already-saved content instead).
   */
  const presetTemplateId = (
    location.state as { presetTemplateId?: string } | null
  )?.presetTemplateId?.trim();
  const appliedPresetTemplateRef = useRef(false);
  const { data: presetTemplate } = useGetTemplate(presetTemplateId ?? '', {
    enabled: Boolean(presetTemplateId) && !isEditMode,
  });

  useEffect(() => {
    if (!presetTemplate || appliedPresetTemplateRef.current || isEditMode) return;
    appliedPresetTemplateRef.current = true;
    setSelectedTemplateId(presetTemplate.itemId);
    setSelectedTemplateName(presetTemplate.name?.trim() || 'Untitled template');
    setLastSavedHtml(presetTemplate.html ?? '');
    setTemplateType('Template Gallery');
  }, [presetTemplate, isEditMode]);

  const trimmedCampaignName = campaignName.trim();
  const isCampaignNameValid = trimmedCampaignName.length > 0 && trimmedCampaignName.length <= 100;
  const campaignNameError = !trimmedCampaignName
    ? 'Campaign name is required'
    : campaignName.length > 100
      ? 'Campaign name must be 100 characters or less'
      : '';
  const trimmedSubject = subject.trim();
  const isSubjectValid = trimmedSubject.length > 0;
  const subjectError = isSubjectValid ? '' : 'Subject is required.';
  const trimmedFromEmail = fromEmail.trim();
  const fromEmailValidation = fromEmailSchema.safeParse(fromEmail);
  const isFromEmailValid = fromEmailValidation.success;
  const fromEmailError = fromEmailValidation.success
    ? ''
    : (fromEmailValidation.error.issues[0]?.message ?? 'Enter a valid email address.');
  const trimmedFromName = fromName.trim();
  const isFromNameValid = trimmedFromName.length > 0;
  const fromNameError = isFromNameValid ? '' : 'From name is required.';
  const trimmedReplyTo = replyTo.trim();
  const replyToValidation = fromEmailSchema.safeParse(replyTo);
  const isReplyToValid = replyToValidation.success;
  const replyToError = replyToValidation.success
    ? ''
    : (replyToValidation.error.issues[0]?.message ?? 'Enter a valid email address.');
  const trimmedPreheader = preheader.trim();
  const isDetailsStepValid =
    isCampaignNameValid &&
    isSubjectValid &&
    isFromEmailValid &&
    isFromNameValid &&
    isReplyToValid;

  const shouldLoadAudienceData = currentStep === 2 || currentStep === 4 || currentStep === 5;
  const {
    data: listsData,
    isLoading: isListsLoading,
    isError: isListsError,
  } = useGetLists(
    {
      pageNo: 1,
      pageSize: 100,
    },
    { enabled: shouldLoadAudienceData }
  );
  const { data: segmentsData } = useGetSegments(
    { pageNo: 1, pageSize: 100, sort: 'updated', order: 'desc' },
    { enabled: shouldLoadAudienceData }
  );
  const lists = useMemo(() => listsData?.items ?? [], [listsData?.items]);
  const segments = useMemo(() => segmentsData?.items ?? [], [segmentsData?.items]);
  const scheduleAudienceListNames = useMemo(() => {
    const byId = new Map(lists.map((list) => [list.id, list.name.trim() || 'Untitled list']));
    return audienceSelection.includeListIds.map(
      (id) => byId.get(id) || 'Untitled list'
    );
  }, [audienceSelection.includeListIds, lists]);
  const scheduleAudienceSegmentNames = useMemo(() => {
    const byId = new Map(
      segments.map((segment) => [segment.itemId, segment.name.trim() || 'Untitled segment'])
    );
    return audienceSelection.includeSegmentIds.map(
      (id) => byId.get(id) || 'Untitled segment'
    );
  }, [audienceSelection.includeSegmentIds, segments]);
  const audienceSelectionValid = isAudienceSelectionValid(audienceSelection);
  const recipientCount = audienceSelectionValid ? audienceCounts.sendable : 0;
  const hasMinimumRecipients = audienceSelectionValid && recipientCount >= 1;
  const hasCampaignContent =
    Boolean(lastSavedHtml.trim()) ||
    Boolean(selectedTemplateId.trim()) ||
    (isHydrated && campaign?.contentHasHtml === true);
  const isRecurringScheduleValid =
    sendType !== 'Recurring' ||
    startOfDay(recurringEndDate).getTime() >= startOfDay(recurringStartDate).getTime();
  const scheduleInstant =
    sendType === 'Scheduled'
      ? parse(scheduleTime || '00:00', 'HH:mm', startOfDay(scheduleDate)).getTime()
      : sendType === 'Recurring'
        ? parse(scheduleTime || '00:00', 'HH:mm', startOfDay(recurringStartDate)).getTime()
        : null;
  const scheduleNeedsFutureTime = sendType === 'Scheduled' || sendType === 'Recurring';
  const isScheduleTimeValid =
    !scheduleNeedsFutureTime ||
    (typeof scheduleInstant === 'number' &&
      !Number.isNaN(scheduleInstant) &&
      scheduleInstant >= Date.now() + MIN_SCHEDULE_AHEAD_MS);
  const scheduleError = scheduleNeedsFutureTime && !isScheduleTimeValid
    ? 'Pick a time at least 6 minutes from now.'
    : '';
  const canGoNext = DESIGN_PREVIEW_MODE
    ? true
    : currentStep === 1
      ? isDetailsStepValid
      : currentStep === 2
        ? audienceSelectionValid
        : currentStep === 3
          ? hasCampaignContent
          : currentStep === 4
            ? isRecurringScheduleValid && isScheduleTimeValid
            : true;
  const contentSummaryLabel =
    templateType === 'Blank Template'
      ? selectedTemplateName
        ? `Blank: ${selectedTemplateName}`
        : 'Blank Email Campaign'
      : templateType === 'Template Gallery'
        ? selectedTemplateName
          ? `Gallery: ${selectedTemplateName}`
          : 'Gallery Email Campaign'
        : templateType === 'Saved Templates'
          ? selectedTemplateName
            ? `Saved: ${selectedTemplateName}`
            : 'Saved Email Campaign'
          : 'Email Campaign';

  const goPreviousStep = () => setCurrentStep((prev) => Math.max(1, prev - 1));

  const getSupportedDraftInput = (): UpdateCampaignRestInput => ({
    name: trimmedCampaignName,
    subject: trimmedSubject,
    preheader: trimmedPreheader,
    fromName: trimmedFromName,
    fromEmail: trimmedFromEmail,
    replyTo: trimmedReplyTo || undefined,
  });

  /** The wizard's schedule as the draft stores it; `null` (send now) clears a saved plan. */
  const getScheduleDraft = (): CampaignScheduleDraft | null =>
    sendType === 'Regular'
      ? null
      : {
          sendType,
          date: format(sendType === 'Recurring' ? recurringStartDate : scheduleDate, 'yyyy-MM-dd'),
          time: scheduleTime,
          timeZone,
          recurringFrequency,
        };

  const goNextStep = async () => {
    if (DESIGN_PREVIEW_MODE) {
      setCurrentStep((prev) => Math.min(WIZARD_STEP_COUNT, prev + 1));
      return;
    }

    if (currentStep === 1 && !isDetailsStepValid) {
      setCampaignNameTouched(true);
      setSubjectTouched(true);
      setFromEmailTouched(true);
      setFromNameTouched(true);
      setReplyToTouched(true);
      return;
    }
    if (currentStep === 4 && sendType === 'Recurring' && !isRecurringScheduleValid) {
      return;
    }
    if (currentStep === 4 && !isScheduleTimeValid) {
      toast({
        variant: 'destructive',
        title: 'Schedule time required',
        description: scheduleError || 'Pick a time at least 6 minutes from now.',
      });
      return;
    }
    if (currentStep === 2 && !audienceSelectionValid) {
      toast({
        variant: 'destructive',
        title: 'No audience selected',
        description: 'Select at least one list or segment.',
      });
      return;
    }

    if (currentStep === 3 && !hasCampaignContent) {
      toast({
        variant: 'destructive',
        title: 'Content required',
        description: 'Add content before continuing — template, copy, or blank.',
      });
      return;
    }

    if (currentStep === 2) {
      try {
        if (!effectiveCampaignId) {
          throw new Error('Save campaign details before setting audience');
        }
        await setAudience({
          campaignId: effectiveCampaignId,
          input: audienceSelectionToRestInput(audienceSelection),
        });
        setAudienceDirty(false);
        setCurrentStep(3);
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Failed to save audience',
          description: error instanceof Error ? error.message : 'Please try again.',
        });
      }
      return;
    }

    if (currentStep === 3) {
      try {
        if (!effectiveCampaignId) {
          throw new Error('Save campaign details before continuing');
        }
        const input = getSupportedDraftInput();
        await updateCampaign({
          campaignId: effectiveCampaignId,
          input,
        });

        // Guide §4 / §12 step 5 — content must exist on the server before send.
        // Skip put when Save content / apply-template / blank already persisted it.
        if (lastSavedHtml.trim()) {
          if (isContentDirty) {
            const saved = await putContent({
              campaignId: effectiveCampaignId,
              input: { html: lastSavedHtml, plainText: null },
            });
            setLastSavedHtml(saved.html);
            if (saved.contentId) {
              setSelectedContentId(saved.contentId);
            }
            setIsContentDirty(false);
          }
        } else if (selectedTemplateId.trim()) {
          const applied = await applyTemplate({
            campaignId: effectiveCampaignId,
            input: { templateId: selectedTemplateId },
          });
          setLastSavedHtml(applied.html);
          if (applied.contentId) {
            setSelectedContentId(applied.contentId);
          }
          setIsContentDirty(false);
        }

        setCurrentStep(4);
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Failed to save campaign content',
          description: error instanceof Error ? error.message : 'Please try again.',
        });
      }
      return;
    }

    if (currentStep === 4) {
      try {
        if (!effectiveCampaignId) {
          throw new Error('Save campaign details before scheduling');
        }
        await updateCampaign({
          campaignId: effectiveCampaignId,
          input: { draftSchedule: getScheduleDraft() },
        });
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Failed to save schedule',
          description: error instanceof Error ? error.message : 'Please try again.',
        });
        return;
      }
    }

    if (currentStep !== 1) {
      setCurrentStep((prev) => Math.min(WIZARD_STEP_COUNT, prev + 1));
      return;
    }

    const detailsInput: CreateCampaignRestInput = {
      ...getSupportedDraftInput(),
      name: trimmedCampaignName,
      channel: 'email',
      // Guide §2.1: templateId is accepted but has no effect — apply-template after create.
    };

    try {
      if (!effectiveCampaignId) {
        const created = await createCampaign(detailsInput);
        setDraftCampaignId(created.id);
        if (selectedTemplateId.trim() || lastSavedHtml.trim()) {
          if (selectedTemplateId.trim()) {
            const applied = await applyTemplate({
              campaignId: created.id,
              input: { templateId: selectedTemplateId },
            });
            setLastSavedHtml(applied.html);
            if (applied.contentId) setSelectedContentId(applied.contentId);
          } else if (lastSavedHtml.trim()) {
            const saved = await putContent({
              campaignId: created.id,
              input: { html: lastSavedHtml, plainText: null },
            });
            setLastSavedHtml(saved.html);
            if (saved.contentId) setSelectedContentId(saved.contentId);
          }
        }
        clearWizardStepFromSession();
        writeWizardStepToSession(2, created.id);
        navigate(`/campaigns/edit/${created.id}`, {
          replace: true,
          state: { wizardStep: 2, wizardStepVersion: WIZARD_STEP_VERSION },
        });
        setCurrentStep(2);
        return;
      }

      await updateCampaign({
        campaignId: effectiveCampaignId,
        input: getSupportedDraftInput(),
      });
      setCurrentStep(2);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to save draft',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const validatePublishReadiness = (): boolean => {
    if (DESIGN_PREVIEW_MODE) return true;

    const firstInvalidStep = getFirstInvalidCampaignStep({
      detailsValid: isDetailsStepValid,
      audienceValid: hasMinimumRecipients,
      contentValid: hasCampaignContent,
      scheduleValid: isRecurringScheduleValid && isScheduleTimeValid,
    });
    if (firstInvalidStep === null) return true;

    setSendConfirmOpen(false);
    setCurrentStep(firstInvalidStep);

    if (firstInvalidStep === 1) {
      setCampaignNameTouched(true);
      setSubjectTouched(true);
      setFromEmailTouched(true);
      setFromNameTouched(true);
      setReplyToTouched(true);
    }

    const descriptions: Record<number, string> = {
      1: 'Complete the required campaign name, subject, from name, from email, and reply-to fields.',
      2: 'Select at least one list or segment.',
      3: 'Add campaign content before sending.',
      4: 'Correct the campaign schedule.',
    };
    toast({
      variant: 'destructive',
      title: 'Campaign not ready',
      description: descriptions[firstInvalidStep],
    });
    return false;
  };

  const handleSendCampaignConfirm = async () => {
    if (!validatePublishReadiness()) return;

    if (DESIGN_PREVIEW_MODE) {
      toast({
        title: 'Design preview',
        description: 'Campaign persistence is disabled. Re-enable DESIGN_PREVIEW_MODE=false later.',
      });
      setSendConfirmOpen(false);
      return;
    }

    if (!effectiveCampaignId) {
      setSendConfirmOpen(false);
      toast({
        variant: 'destructive',
        title: sendType === 'Regular' ? 'Cannot send' : 'Cannot schedule',
        description: 'Save campaign details before continuing.',
      });
      return;
    }

    try {
      if (!sendIdempotencyKeyRef.current) {
        sendIdempotencyKeyRef.current =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `send-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }

      if (sendType === 'Regular') {
        await sendCampaign({
          campaignId: effectiveCampaignId,
          idempotencyKey: sendIdempotencyKeyRef.current,
          confirmEstimatedRecipients: lastConfirmSendableRef.current,
        });
        toast({
          variant: 'success',
          title: 'Campaign queued',
          description: 'Send was accepted and is being processed.',
        });
      } else {
        const scheduleInput = buildScheduleCampaignRestInput({
          campaignId: effectiveCampaignId,
          idempotencyKey: sendIdempotencyKeyRef.current,
          sendType,
          scheduleDate,
          scheduleTime,
          timeZone,
          recurringFrequency,
          recurringStartDate,
        });
        await scheduleCampaign(scheduleInput);
        toast({
          variant: 'success',
          title: sendType === 'Scheduled' ? 'Campaign scheduled' : 'Recurring schedule created',
          description:
            sendType === 'Scheduled'
              ? 'This campaign will send at the scheduled time.'
              : 'The recurring series was queued. Runs are created as they become due.',
        });
      }

      sendIdempotencyKeyRef.current = null;
      lastConfirmSendableRef.current = undefined;
      setSendConfirmOpen(false);
      clearWizardStepFromSession(effectiveCampaignId);
      clearWizardStepFromSession();
      navigate('/campaigns');
    } catch (error) {
      toast({
        variant: 'destructive',
        title: sendType === 'Regular' ? 'Failed to send campaign' : 'Failed to schedule campaign',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const handleSaveAsDraft = async () => {
    if (DESIGN_PREVIEW_MODE) {
      toast({
        title: 'Design preview',
        description: 'Campaign persistence is disabled. Re-enable DESIGN_PREVIEW_MODE=false later.',
      });
      return;
    }

    if (!isCampaignNameValid) {
      setCampaignNameTouched(true);
      if (currentStep !== 1) {
        setCurrentStep(1);
      }
      toast({
        variant: 'destructive',
        title: 'Campaign name required',
        description: 'Enter a campaign name before saving as draft.',
      });
      return;
    }

    try {
      let persistedCampaignId = effectiveCampaignId;
      const shouldNavigateToEdit = !campaignId?.trim();
      const input = getSupportedDraftInput();
      const draftSchedule = getScheduleDraft();

      if (!persistedCampaignId) {
        const created = await createCampaign({
          ...input,
          name: trimmedCampaignName,
          channel: 'email',
        });
        persistedCampaignId = created.id;
        setDraftCampaignId(created.id);
        // Create takes no schedule; a new draft has none to clear, so PATCH only to save one.
        if (draftSchedule) {
          await updateCampaign({ campaignId: persistedCampaignId, input: { draftSchedule } });
        }
      } else {
        // Always PATCH so Save as draft persists the latest wizard values,
        // even when they match the last in-memory snapshot (e.g. Review step).
        await updateCampaign({ campaignId: persistedCampaignId, input: { ...input, draftSchedule } });
      }

      if (lastSavedHtml.trim()) {
        const saved = await putContent({
          campaignId: persistedCampaignId,
          input: { html: lastSavedHtml, plainText: null },
        });
        setLastSavedHtml(saved.html);
        if (saved.contentId) setSelectedContentId(saved.contentId);
      } else if (selectedTemplateId.trim()) {
        const applied = await applyTemplate({
          campaignId: persistedCampaignId,
          input: { templateId: selectedTemplateId },
        });
        setLastSavedHtml(applied.html);
        if (applied.contentId) setSelectedContentId(applied.contentId);
      }

      if (audienceSelectionValid) {
        await setAudience({
          campaignId: persistedCampaignId,
          input: audienceSelectionToRestInput(audienceSelection),
        });
        setAudienceDirty(false);
      }

      if (shouldNavigateToEdit) {
        clearWizardStepFromSession();
        writeWizardStepToSession(currentStep, persistedCampaignId);
      }

      toast({
        variant: 'success',
        title: 'Draft saved',
        description:
          sendType === 'Regular'
            ? `"${trimmedCampaignName}" was saved as a draft.`
            : `"${trimmedCampaignName}" was saved as a draft, including its schedule.`,
      });
      if (shouldNavigateToEdit) {
        navigate(`/campaigns/edit/${persistedCampaignId}`, {
          replace: true,
          state: {
            wizardStep: currentStep,
            wizardStepVersion: WIZARD_STEP_VERSION,
          },
        });
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to save draft',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  const handleReviewSendClick = async () => {
    if (!validatePublishReadiness()) return;

    if (!effectiveCampaignId) {
      toast({
        variant: 'destructive',
        title: sendType === 'Regular' ? 'Cannot send' : 'Cannot schedule',
        description: 'Save campaign details before continuing.',
      });
      return;
    }

    setIsCheckingEligibility(true);
    try {
      if (audienceDirty) {
        await setAudience({
          campaignId: effectiveCampaignId,
          input: audienceSelectionToRestInput(audienceSelection),
        });
        setAudienceDirty(false);
      }

      const eligibility = await getCampaignSendEligibility(effectiveCampaignId);
      if (!eligibility.sendable) {
        const blocking = eligibility.blocked.filter(
          (item) => item.severity !== 'warning'
        );
        toast({
          variant: 'destructive',
          title: sendType === 'Regular' ? 'Cannot send' : 'Cannot schedule',
          description:
            blocking.map((item) => item.message).join(' ') ||
            eligibility.blockedMessages.join(' ') ||
            'Campaign is not sendable.',
        });
        return;
      }

      lastConfirmSendableRef.current =
        eligibility.recipients?.sendable ?? audienceCounts.sendable;
      sendIdempotencyKeyRef.current =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `send-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setSendConfirmOpen(true);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Failed to check send eligibility',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setIsCheckingEligibility(false);
    }
  };

  const recurringSummaryLabel = `${recurringFrequency} at ${formatScheduleTimeDisplay(scheduleTime)}`;

  const pageTitle = isEditMode
    ? `Edit ${trimmedCampaignName ? `“${trimmedCampaignName}”` : 'campaign'}`
    : 'New campaign';
  const lastUpdatedLabel = formatCampaignDate(campaign?.updatedAt);
  const pageSubtitle = isEditMode
    ? lastUpdatedLabel
      ? `Draft · last updated ${lastUpdatedLabel}`
      : 'Draft'
    : 'A campaign is saved as a draft the moment you name it.';

  const sendTypeSummary =
    sendType === 'Scheduled' && scheduleDate
      ? format(
          parse(
            `${format(scheduleDate, 'yyyy-MM-dd')} ${scheduleTime}`,
            'yyyy-MM-dd HH:mm',
            new Date()
          ),
          "MMM d, yyyy 'at' h:mm a"
        ) + ` (${formatTimezoneLabel(timeZone)})`
      : sendType === 'Recurring'
        ? `${recurringSummaryLabel} (${formatTimezoneLabel(timeZone)}) · starts ${format(recurringStartDate, 'MMM d, yyyy')}`
        : 'Immediately, on confirm';

  const reviewAudienceLabel = audienceSelectionValid
    ? `${audienceCounts.audience.toLocaleString()} ${audienceCounts.audience === 1 ? 'contact' : 'contacts'} · ${recipientCount.toLocaleString()} sendable`
    : 'Not set';

  const reviewContentLabel = hasCampaignContent
    ? lastSavedHtml.trim()
      ? contentSummaryLabel
      : campaign?.contentHasHtml
        ? 'Content saved on server'
        : contentSummaryLabel
    : 'Not set';

  const reviewChecks = useMemo((): CampaignReviewCheck[] => {
    return [
      {
        id: 'content',
        label: 'Content saved',
        pass: hasCampaignContent,
        detail: hasCampaignContent ? undefined : 'This campaign has no saved content yet.',
        severity: 'blocking',
        fixStep: 3,
      },
      {
        id: 'subject',
        label: 'Subject line set',
        pass: isSubjectValid,
        severity: 'blocking',
        fixStep: 1,
      },
      {
        id: 'sender',
        label: 'Sender identity set',
        pass: isFromEmailValid && isFromNameValid,
        severity: 'blocking',
        fixStep: 1,
      },
      {
        id: 'audience',
        label: 'Audience selected',
        pass: audienceSelectionValid,
        detail: audienceSelectionValid ? undefined : 'Pick at least one list or segment.',
        severity: 'blocking',
        fixStep: 2,
      },
      {
        id: 'sendable',
        label: 'At least one sendable recipient',
        pass: hasMinimumRecipients,
        detail: hasMinimumRecipients
          ? undefined
          : 'Every contact in this audience is unsubscribed, pending or suppressed.',
        severity: 'blocking',
        fixStep: 2,
      },
      {
        id: 'plaintext',
        label: 'Plain-text version present',
        pass: false,
        detail: 'Recommended for deliverability.',
        severity: 'advisory',
        fixStep: 3,
      },
      {
        id: 'spamscore',
        label: 'Spam-score check',
        pass: false,
        detail:
          'Content scoring against common spam filters is not part of this milestone. Nothing is evaluated, and nothing is blocked by it.',
        severity: 'deferred',
        milestone: 'MVP-3',
      },
    ];
  }, [
    audienceSelectionValid,
    hasCampaignContent,
    hasMinimumRecipients,
    isFromEmailValid,
    isFromNameValid,
    isSubjectValid,
  ]);

  const reviewChecksOk = reviewChecks.every(
    (check) => check.pass || check.severity !== 'blocking'
  );

  if (isEditMode && isCampaignLoading && !DESIGN_PREVIEW_MODE) {
    return (
      <div className="flex h-full w-full items-center justify-center rounded-xl border bg-card p-6">
        <p className="text-sm text-muted-foreground">Loading campaign...</p>
      </div>
    );
  }

  if (
    isEditMode &&
    campaign &&
    !DESIGN_PREVIEW_MODE &&
    !canEditCampaign(campaign.status)
  ) {
    return null;
  }

  if (
    isEditMode &&
    !DESIGN_PREVIEW_MODE &&
    (isCampaignError || !campaign || !isHydrated)
  ) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-3 rounded-xl border bg-card p-6 text-center">
        <WarningCircle className="size-8 text-destructive" aria-hidden />
        <h1 className="text-lg font-semibold text-foreground">
          {isCampaignNotFound ? 'Campaign not found' : 'Could not load this campaign'}
        </h1>
        <p className="max-w-[360px] text-sm text-muted-foreground">
          {isCampaignNotFound
            ? 'This campaign may have been deleted, or the link is out of date.'
            : campaignError instanceof Error
              ? campaignError.message
              : 'Something went wrong on our end. Try again in a moment.'}
        </p>
        <Button type="button" onClick={() => navigate('/campaigns')}>
          Back to Campaigns
        </Button>
      </div>
    );
  }

  // Audience (step 2) and the full-screen content editor fill the viewport; other steps size to content.
  const fillRemainingStep = contentEditorActive || currentStep === 2;
  const wizardMaxWidth = 'max-w-[960px]';

  return (
    <div
      className={cn(
        'flex h-full min-w-0 w-full flex-col gap-5 overflow-x-hidden',
        fillRemainingStep ? 'overflow-y-hidden' : 'overflow-y-auto'
      )}
    >
      {!contentEditorActive ? (
        <div className={cn('mx-auto flex w-full flex-none flex-wrap items-start gap-4', wizardMaxWidth)}>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">{pageTitle}</h1>
            <p className="mt-0.5 text-sm text-neutral-600">{pageSubtitle}</p>
            {DESIGN_PREVIEW_MODE ? (
              <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-950">
                Design preview: campaign saves and step validation are off. Click any step
                above, or use Next/Back. Set <code>DESIGN_PREVIEW_MODE</code> to false when done.
              </p>
            ) : null}
          </div>
          <div className="flex-1" />
          <button
            type="button"
            className="shrink-0 text-sm font-medium text-neutral-600 hover:text-neutral-900"
            onClick={() => navigate('/campaigns')}
          >
            Cancel
          </button>
        </div>
      ) : null}

      <div
        className={cn(
          'mx-auto flex w-full flex-col gap-5 pb-2',
          contentEditorActive ? 'max-w-[1320px]' : wizardMaxWidth,
          fillRemainingStep && 'min-h-0 flex-1 overflow-hidden'
        )}
      >
        {!contentEditorActive ? (
        <div className="flex flex-none flex-wrap items-center gap-2" aria-label="Campaign steps">
          {STEP_TITLES.map((title, index) => {
            const step = index + 1;
            const state =
              currentStep > step ? 'done' : currentStep === step ? 'active' : 'pending';
            return (
              <div key={title} className="flex items-center gap-2">
                {index > 0 ? (
                  <span className="h-px w-8 shrink-0 bg-neutral-200" aria-hidden />
                ) : null}
                <button
                  type="button"
                  disabled={!DESIGN_PREVIEW_MODE}
                  aria-current={state === 'active' ? 'step' : undefined}
                  onClick={() => {
                    if (DESIGN_PREVIEW_MODE) setCurrentStep(step);
                  }}
                  className={cn(
                    'flex items-center gap-2',
                    DESIGN_PREVIEW_MODE ? 'cursor-pointer' : 'cursor-default'
                  )}
                >
                  <span
                    className={cn(
                      'inline-flex size-7 shrink-0 items-center justify-center rounded-full text-[13px] font-semibold',
                      state === 'active' && 'bg-primary-600 text-white',
                      state === 'done' && 'bg-[#1D8242] text-white',
                      state === 'pending' && 'bg-neutral-100 text-neutral-600'
                    )}
                  >
                    {step}
                  </span>
                  <span
                    className={cn(
                      'text-[13px] font-medium text-neutral-600',
                      (state === 'active' || state === 'done') &&
                        'font-semibold text-neutral-900'
                    )}
                  >
                    {title}
                  </span>
                </button>
              </div>
            );
          })}
        </div>
        ) : null}

        {currentStep === 1 && (
          <div className="flex-none">
            <div className="space-y-5 rounded-lg border border-neutral-200 bg-white p-4 shadow-e1 md:p-6">
            <div className="space-y-2">
              <Label>Channel</Label>
              <div className="flex items-stretch gap-3">
                <div className="flex flex-1 items-center gap-2 rounded-lg border border-primary-600 bg-primary-50 px-4 py-3 text-primary-600">
                  <Envelope className="size-4 shrink-0" aria-hidden />
                  <span className="font-medium leading-none">Email</span>
                </div>
                <div
                  aria-disabled="true"
                  className="flex flex-1 flex-col justify-center rounded-lg border border-border bg-muted/30 px-4 py-3 text-muted-foreground opacity-60"
                >
                  <div className="flex items-center gap-2">
                    <DeviceMobile className="size-4 shrink-0" aria-hidden />
                    <span className="font-medium leading-none">SMS</span>
                  </div>
                  <p className="mt-0.5 text-xs leading-snug">MVP-3 — not yet available</p>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="campaign-name">
                Campaign name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="campaign-name"
                placeholder="Enter campaign name"
                value={campaignName}
                maxLength={100}
                aria-invalid={campaignNameTouched && !isCampaignNameValid}
                onBlur={() => setCampaignNameTouched(true)}
                onChange={(e) => setCampaignName(e.target.value)}
                className={
                  campaignNameTouched && !isCampaignNameValid
                    ? 'border-destructive focus-visible:ring-destructive'
                    : undefined
                }
              />
              <div className="flex items-center justify-between gap-2">
                {campaignNameTouched && campaignNameError ? (
                  <p className="text-xs text-destructive">{campaignNameError}</p>
                ) : (
                  <span />
                )}
                <p className="text-xs text-muted-foreground text-right">{campaignName.length}/100</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="campaign-subject">
                Subject line *{' '}
                <span
                  className={
                    subject.length > SUBJECT_RECOMMENDED_LENGTH
                      ? 'font-medium text-amber-700'
                      : 'font-normal text-muted-foreground'
                  }
                >
                  · {subject.length}/{SUBJECT_RECOMMENDED_LENGTH} characters recommended
                </span>
              </Label>
              <Input
                id="campaign-subject"
                placeholder="What lands in the inbox"
                value={subject}
                aria-invalid={subjectTouched && !isSubjectValid}
                aria-describedby={
                  subjectTouched && subjectError ? 'campaign-subject-error' : undefined
                }
                onBlur={() => setSubjectTouched(true)}
                onChange={(e) => setSubject(e.target.value)}
                className={
                  subjectTouched && !isSubjectValid
                    ? 'border-destructive focus-visible:ring-destructive'
                    : undefined
                }
              />
              {subjectTouched && subjectError ? (
                <p id="campaign-subject-error" className="text-xs text-destructive" role="alert">
                  {subjectError}
                </p>
              ) : subject.length > SUBJECT_RECOMMENDED_LENGTH ? (
                <p className="flex items-center gap-1 text-xs text-amber-700" role="status">
                  <Warning className="size-3.5 shrink-0" aria-hidden />
                  Long subjects may be cut off in inboxes. You can still continue.
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="campaign-preheader">
                Preview text{' '}
                <span className="font-normal text-muted-foreground">
                  (optional) · {preheader.length}/{PREHEADER_RECOMMENDED_LENGTH} characters
                </span>
              </Label>
              <Input
                id="campaign-preheader"
                placeholder="The short line shown after the subject in the inbox"
                value={preheader}
                maxLength={PREHEADER_MAX_LENGTH}
                onChange={(e) => setPreheader(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Inbox preview:{' '}
                <span className="font-medium text-foreground">
                  {subject.trim() || 'Your subject'}
                </span>
                {' — '}
                {preheader.trim() || 'your preview text appears here'}
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="campaign-from-name">From name *</Label>
                <Input
                  id="campaign-from-name"
                  placeholder="SELISE Cloud"
                  value={fromName}
                  aria-invalid={fromNameTouched && !isFromNameValid}
                  onBlur={() => setFromNameTouched(true)}
                  onChange={(e) => setFromName(e.target.value)}
                  className={
                    fromNameTouched && !isFromNameValid
                      ? 'border-destructive focus-visible:ring-destructive'
                      : undefined
                  }
                />
                {fromNameTouched && fromNameError ? (
                  <p className="text-xs text-destructive" role="alert">
                    {fromNameError}
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="campaign-from-email">From email *</Label>
                <Input
                  id="campaign-from-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="hello@yourcompany.com"
                  value={fromEmail}
                  aria-invalid={fromEmailTouched && !isFromEmailValid}
                  aria-describedby={
                    fromEmailTouched && fromEmailError ? 'campaign-from-email-error' : undefined
                  }
                  onBlur={() => setFromEmailTouched(true)}
                  onChange={(e) => setFromEmail(e.target.value)}
                  className={
                    fromEmailTouched && !isFromEmailValid
                      ? 'border-destructive focus-visible:ring-destructive'
                      : undefined
                  }
                />
                {fromEmailTouched && fromEmailError ? (
                  <p id="campaign-from-email-error" className="text-xs text-destructive" role="alert">
                    {fromEmailError}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Recipients see this address as the sender.
                  </p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="campaign-reply-to">Reply-to email *</Label>
              <Input
                id="campaign-reply-to"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="hello@yourcompany.com"
                value={replyTo}
                aria-invalid={replyToTouched && !isReplyToValid}
                onBlur={() => setReplyToTouched(true)}
                onChange={(e) => setReplyTo(e.target.value)}
                className={
                  replyToTouched && !isReplyToValid
                    ? 'border-destructive focus-visible:ring-destructive'
                    : undefined
                }
              />
              {replyToTouched && replyToError ? (
                <p className="text-xs text-destructive" role="alert">
                  {replyToError}
                </p>
              ) : null}
            </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="min-h-0 flex-1 overflow-hidden">
            <CampaignAudienceStep
              selection={audienceSelection}
              onChange={(selection) => {
                setAudienceSelection(selection);
                setAudienceDirty(true);
              }}
              lists={lists}
              isListsLoading={isListsLoading}
              isListsError={isListsError}
              campaignId={effectiveCampaignId}
              onAudienceCounts={setAudienceCounts}
              isPublishAudienceResolved
              designPreview={DESIGN_PREVIEW_MODE}
            />
          </div>
        )}

        {currentStep === 3 && (
          <div className={cn(contentEditorActive ? 'min-h-0 flex-1 overflow-hidden' : 'flex-none')}>
            <CampaignContentStep
              campaignId={effectiveCampaignId}
              campaignName={trimmedCampaignName}
              subject={subject}
              html={lastSavedHtml}
              selectedTemplateId={selectedTemplateId}
              selectedTemplateName={selectedTemplateName}
              savedAt={campaign?.updatedAt || campaign?.createdAt}
              onEditorActiveChange={setContentEditorActive}
              onHtmlChange={(nextHtml) => {
                setLastSavedHtml(nextHtml);
                setIsContentDirty(true);
                setTemplateType('Blank Template');
              }}
              onContentSynced={({ html, contentId }) => {
                setLastSavedHtml(html);
                if (contentId?.trim()) {
                  setSelectedContentId(contentId.trim());
                }
                setIsContentDirty(false);
              }}
              onTemplateApplied={({ html, templateId, templateName, contentId, synced }) => {
                setLastSavedHtml(html);
                setSelectedTemplateId(templateId);
                setSelectedTemplateName(templateName);
                setSelectedContentId(contentId?.trim() || '');
                setTemplateType('Template Gallery');
                if (synced !== undefined) {
                  setIsContentDirty(!synced);
                }
              }}
            />
          </div>
        )}

        {currentStep === 4 && (
          <div className="flex-none">
            <CampaignScheduleStep
              sendType={sendType}
              onSendTypeChange={(value) => {
                setSendType(value);
                if (value !== 'Regular') {
                  // A default picked earlier may have slipped into the past while the wizard was open.
                  const current = parse(
                    scheduleTime || '00:00',
                    'HH:mm',
                    startOfDay(value === 'Recurring' ? recurringStartDate : scheduleDate)
                  ).getTime();
                  if (Number.isNaN(current) || current < Date.now() + MIN_SCHEDULE_AHEAD_MS) {
                    const slot = getDefaultScheduleSlot();
                    setScheduleDate(slot.date);
                    setRecurringStartDate(slot.date);
                    setScheduleTime(slot.time);
                  }
                }
                if (value === 'Recurring') {
                  setRecurringEndDate(
                    getRecurringEndDateForFrequency(recurringStartDate, recurringFrequency)
                  );
                }
              }}
              scheduleDate={scheduleDate}
              scheduleTime={scheduleTime}
              onScheduleDateTimeChange={(date, time) => {
                setScheduleDate(date);
                setScheduleTime(time);
              }}
              timeZone={timeZone}
              onTimeZoneChange={setTimeZone}
              recurringFrequency={recurringFrequency}
              onRecurringFrequencyChange={(frequency) => {
                setRecurringFrequency(frequency);
                setRecurringEndDate(getRecurringEndDateForFrequency(recurringStartDate, frequency));
              }}
              recurringStartDate={recurringStartDate}
              onRecurringStartDateChange={(date) => {
                setRecurringStartDate(date);
                setRecurringEndDate(getRecurringEndDateForFrequency(date, recurringFrequency));
              }}
              scheduleError={scheduleError}
              audiencePreview={{
                audienceCount: audienceSelectionValid ? audienceCounts.audience : 0,
                sendableCount: audienceSelectionValid ? audienceCounts.sendable : 0,
                listNames: scheduleAudienceListNames,
                segmentNames: scheduleAudienceSegmentNames,
              }}
              contentPreview={{
                html: lastSavedHtml,
                label: reviewContentLabel,
              }}
            />
          </div>
        )}

        {currentStep === 5 && (
          <div className="flex-none">
            <CampaignReviewStep
              subject={trimmedSubject}
              fromName={trimmedFromName}
              fromEmail={trimmedFromEmail}
              audienceLabel={reviewAudienceLabel}
              contentLabel={reviewContentLabel}
              sendingLabel={sendTypeSummary}
              checks={reviewChecks}
              onRefreshChecks={() => {
                toast({
                  variant: 'success',
                  title: 'Checks refreshed',
                });
              }}
              onFixCheck={(step) => setCurrentStep(step)}
            />
          </div>
        )}

        {!contentEditorActive ? (
        <div className="flex flex-none flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={goPreviousStep}
            className={cn(
              'border-neutral-300 bg-white text-neutral-900 hover:bg-neutral-100',
              currentStep === 1 && 'hidden'
            )}
          >
            <CaretLeftIcon className="h-4 w-4" />
            Back
          </Button>
          <div className="flex-1" />
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="ghost" disabled={isSaving} onClick={handleSaveAsDraft}>
              {isSaving ? 'Saving…' : 'Save as draft'}
            </Button>
            {currentStep < WIZARD_STEP_COUNT ? (
              <Button
                type="button"
                onClick={() => void goNextStep()}
                disabled={isSaving || !canGoNext}
                className="bg-primary-600 hover:bg-primary-700 text-white disabled:opacity-50 disabled:pointer-events-none"
              >
                {isSaving ? 'Saving…' : 'Next'}
                <CaretRightIcon className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                className={
                  reviewChecksOk || DESIGN_PREVIEW_MODE
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-neutral-200 text-neutral-500 hover:bg-neutral-200'
                }
                disabled={
                  isSaving ||
                  isSending ||
                  isScheduling ||
                  isCheckingEligibility ||
                  (!DESIGN_PREVIEW_MODE && !reviewChecksOk)
                }
                onClick={() => void handleReviewSendClick()}
              >
                {SEND_TYPE_CTA_LABELS[sendType]}
              </Button>
            )}
          </div>
        </div>
        ) : null}
      </div>

      <ConfirmationModal
        open={sendConfirmOpen}
        onOpenChange={setSendConfirmOpen}
        title={SEND_TYPE_CTA_LABELS[sendType]}
        description={
          sendType === 'Regular'
            ? `Are you sure you want to send "${trimmedCampaignName || 'this campaign'}" to ${recipientCount.toLocaleString()} recipient${recipientCount === 1 ? '' : 's'}? This action cannot be undone.`
            : `"${trimmedCampaignName || 'This campaign'}" goes to ${recipientCount.toLocaleString()} recipient${recipientCount === 1 ? '' : 's'} — ${sendTypeSummary}.`
        }
        confirmText={sendType === 'Regular' ? 'SEND' : 'CONFIRM'}
        cancelText="CANCEL"
        confirmClassName="bg-primary-600 hover:bg-primary-700 text-white"
        onConfirm={handleSendCampaignConfirm}
      />

    </div>
  );
};
